import React, { RefObject, useEffect } from 'react';
import { Button, DatePicker, DatePickerRef, Form, Input, Picker, PickerRef } from 'antd-mobile';
import styles from './index.module.scss';
import { AlarmLevel } from '../AlarmRecord/util';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';

const getOrderedDateRange = (startTime?: Date, endTime?: Date): [Date | undefined, Date | undefined] => {
  if (startTime && endTime && dayjs(startTime).isAfter(endTime)) {
    return [endTime, startTime];
  }
  return [startTime, endTime];
};

const AlarmRecordFilter = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    document.title = '设置筛选条件';
  }, []);

  // 从 URL 参数读取并设置表单默认值
  useEffect(() => {
    const ruleInfo = searchParams.get('ruleInfo');
    const alarmName = searchParams.get('alarmName');
    const startTime = searchParams.get('startTime');
    const endTime = searchParams.get('endTime');
    const levels = searchParams.get('levels');
    const deviceName = searchParams.get('deviceName');

    form.setFieldsValue({
      ruleInfo: ruleInfo || undefined,
      alarmName: alarmName || undefined,
      startTime: startTime ? dayjs(Number(startTime)).toDate() : undefined,
      endTime: endTime ? dayjs(Number(endTime)).toDate() : undefined,
      level: levels ? [Number(levels)] : [AlarmLevel.ALL],
      deviceName: deviceName || undefined,
    });
  }, []);

  // 近7日
  const handleNear7Days = () => {
    const now = dayjs();
    const start = now.subtract(7, 'day');
    form.setFieldsValue({
      startTime: start.toDate(),
      endTime: now.toDate(),
    });
  };

  // 近30日
  const handleNear30Days = () => {
    const now = dayjs();
    const start = now.subtract(30, 'day');
    form.setFieldsValue({
      startTime: start.toDate(),
      endTime: now.toDate(),
    });
  };

  // 重置
  const handleReset = () => {
    form.resetFields();
  };

  // 查询
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const [startTime, endTime] = getOrderedDateRange(values.startTime, values.endTime);
    const params: Record<string, string> = {};
    if (values.ruleInfo) params.ruleInfo = values.ruleInfo;
    if (values.alarmName) params.alarmName = values.alarmName;
    if (startTime) params.startTime = String(dayjs(startTime).startOf('D').valueOf());
    if (endTime) params.endTime = String(dayjs(endTime).endOf('D').valueOf());
    if (values.level && values.level[0] !== AlarmLevel.ALL) params.levels = values.level[0];
    if (values.deviceName) params.deviceName = values.deviceName;

    const queryString = new URLSearchParams(params).toString();
    const path = queryString
      ? `/intelligent-platform/alarm/record?${queryString}`
      : '/intelligent-platform/alarm/record';
    navigate(path);
  };

  const levelOptions = [
    { label: '全部', value: AlarmLevel.ALL },
    { label: '最高级', value: AlarmLevel.HIGHEST },
    { label: '高级', value: AlarmLevel.HIGH },
  ];

  return (
    <div className={styles.page}>
      <Form form={form} layout="horizontal" mode="card">
        {/* 规则名称 */}
        <Form.Item name="ruleInfo" label="规则名称">
          <Input placeholder="请输入" clearable />
        </Form.Item>

        {/* 报警信息 */}
        <Form.Item name="alarmName" label="报警信息">
          <Input placeholder="请输入" clearable />
        </Form.Item>

        {/* 报警日期快捷按钮 */}
        <Form.Item label="报警日期">
          <div className={styles.dateButtons}>
            <Button size="small" onClick={handleNear7Days}>
              近7日
            </Button>
            <Button size="small" onClick={handleNear30Days}>
              近30日
            </Button>
          </div>
        </Form.Item>

        {/* 开始日期 */}
        <Form.Item
          name="startTime"
          label="开始日期"
          trigger="onConfirm"
          onClick={(e, datePickerRef: RefObject<DatePickerRef>) => {
            datePickerRef.current?.open();
          }}
        >
          <DatePicker>{value => (value ? dayjs(value).format('YYYY-MM-DD') : '请选择')}</DatePicker>
        </Form.Item>

        {/* 结束日期 */}
        <Form.Item
          name="endTime"
          label="结束日期"
          trigger="onConfirm"
          onClick={(e, datePickerRef: RefObject<DatePickerRef>) => {
            datePickerRef.current?.open();
          }}
        >
          <DatePicker>{value => (value ? dayjs(value).format('YYYY-MM-DD') : '请选择')}</DatePicker>
        </Form.Item>

        {/* 报警等级 */}
        <Form.Item
          name="level"
          label="报警等级"
          trigger="onConfirm"
          onClick={(e, pickerRef: RefObject<PickerRef>) => {
            pickerRef.current?.open();
          }}
        >
          <Picker columns={[levelOptions]} defaultValue={[AlarmLevel.ALL]}>
            {value => {
              return value?.[0] ? value[0]?.label : '请选择';
            }}
          </Picker>
        </Form.Item>

        {/* 设备名称 */}
        <Form.Item name="deviceName" label="设备名称">
          <Input placeholder="请输入" clearable />
        </Form.Item>
      </Form>

      {/* 底部按钮 */}
      <div className={styles.footer}>
        <Button block onClick={handleReset}>
          重置
        </Button>
        <Button block color="primary" onClick={handleSearch}>
          查询
        </Button>
      </div>
    </div>
  );
};

export default AlarmRecordFilter;
